import type { ReactNode } from 'react'

import type { SealSlotId } from '../../contracts/api'

import './seal.css'

/**
 * Личная печать — главный визуальный объект Атласа. В центре ядро Ба Цзы,
 * вокруг спутники систем: звезда Цзы Вэй, кин и тон Цолькина, гексаграмма.
 * Непостроенные слои остаются пунктиром — «запечатано», а не спрятано.
 *
 * Геометрия взята из утверждённого макета: пять спутников по кругу радиусом
 * 104 при поле 330, шаг 72° от верхней точки.
 */

export type { SealSlotId }

export interface SealSlot {
  id: SealSlotId
  /** Подпись под спутником: «КИН 255». Пустая — подписи нет. */
  label: string
  /** Вторая строка мелким: «после первого вопроса». */
  note?: string
  /** Цвет системы. */
  color: string
  /** Собран слой или ещё запечатан. */
  sealed?: boolean
  /** Рисунок внутри кружка, в координатах 48×48 с центром 24,24. */
  glyph: ReactNode
}

export interface SealProps {
  /** Иероглиф ствола дня в ядре. Пусто — ядро без знака (время неизвестно). */
  coreGlyph?: string
  coreColor?: string
  slots: SealSlot[]
  /** Ширина в px; вся геометрия масштабируется от 330. */
  size?: number
  /** Печать пары рисуется двумя ядрами — режим включается отдельно. */
  variant?: 'single' | 'small'
}

const FIELD = 330
/** Снизу поле выше: там подписи нижних спутников, и они не должны лечь на орбиту. */
const FIELD_HEIGHT = 336
const CENTER = FIELD / 2
const ORBIT = 104
const SATELLITE = 24

/** Пять мест по кругу, от верхнего по часовой стрелке. */
const SLOT_ORDER: SealSlotId[] = ['ziwei', 'kin', 'iching', 'mewa', 'tone']

function position(index: number): { x: number; y: number } {
  const angle = (index * 72 * Math.PI) / 180
  return {
    x: CENTER + ORBIT * Math.sin(angle),
    y: CENTER - ORBIT * Math.cos(angle),
  }
}

export function Seal({ coreGlyph, coreColor = '#C9924D', slots, size = 330, variant = 'single' }: SealProps) {
  const placed = SLOT_ORDER.map((id, index) => {
    const slot = slots.find((item) => item.id === id)
    return slot ? { slot, ...position(index) } : null
  }).filter((item): item is { slot: SealSlot; x: number; y: number } => item !== null)

  const compact = variant === 'small'

  return (
    <svg
      className="seal"
      viewBox={`0 0 ${FIELD} ${FIELD_HEIGHT}`}
      width={size}
      height={(size * FIELD_HEIGHT) / FIELD}
      role="img"
      aria-label="Твоя личная печать"
    >
      {/* орбиты */}
      <circle cx={CENTER} cy={CENTER} r={158} fill="none" stroke="rgba(199,203,217,.14)" strokeWidth="1" />
      <circle cx={CENTER} cy={CENTER} r={150} fill="none" stroke="rgba(199,203,217,.42)" strokeWidth="1.5" />
      {/* Третьей, частой пунктирной окружности из макета здесь нет намеренно:
          она проходила ровно по подписям нижних спутников и превращала их в кашу. */}
      <circle
        cx={CENTER}
        cy={CENTER}
        r={ORBIT}
        fill="none"
        stroke="rgba(199,203,217,.15)"
        strokeWidth="1"
        strokeDasharray="3 5"
      />

      {/* лучи от ядра к собранным спутникам */}
      <g stroke="rgba(199,203,217,.22)" strokeWidth="1.2">
        {placed.map(({ slot, x, y }) => {
          const dx = x - CENTER
          const dy = y - CENTER
          const length = Math.hypot(dx, dy)
          const from = { x: CENTER + (dx / length) * 56, y: CENTER + (dy / length) * 56 }
          const to = { x: CENTER + (dx / length) * (ORBIT - SATELLITE - 2), y: CENTER + (dy / length) * (ORBIT - SATELLITE - 2) }
          return (
            <line
              key={slot.id}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              opacity={slot.sealed ? 0.4 : 1}
            />
          )
        })}
      </g>

      {/* ядро: Ба Цзы */}
      <circle cx={CENTER} cy={CENTER} r={54} fill="#151827" stroke={coreColor} strokeWidth="2" />
      <circle cx={CENTER} cy={CENTER} r={46} fill="none" stroke="rgba(201,146,77,.35)" strokeWidth="1.5" />
      {coreGlyph ? (
        <text
          x={CENTER}
          y={CENTER + 17}
          textAnchor="middle"
          className="seal-core-glyph"
          fill="#DCA55F"
        >
          {coreGlyph}
        </text>
      ) : (
        // Времени рождения нет — ядро есть, знака часа в нём не появляется.
        <circle cx={CENTER} cy={CENTER} r={16} fill="none" stroke="rgba(220,165,95,.5)" strokeWidth="1.5" strokeDasharray="3 4" />
      )}

      {/* спутники */}
      {placed.map(({ slot, x, y }) => (
        <g key={slot.id} opacity={slot.sealed ? 0.5 : 1}>
          <circle
            cx={x}
            cy={y}
            r={SATELLITE}
            fill={slot.sealed ? '#12141d' : '#141722'}
            stroke={slot.color}
            strokeWidth={slot.sealed ? 1.3 : 1.6}
            strokeDasharray={slot.sealed ? '3 4' : undefined}
          />
          <g transform={`translate(${x - 24} ${y - 24})`}>{slot.glyph}</g>
        </g>
      ))}

      {/* подписи — только когда печать крупная: на маленькой они не читаются */}
      {!compact &&
        placed.map(({ slot, x, y }) => {
          // Верхние спутники подписаны над собой, нижние — ниже внешней орбиты:
          // если поставить подпись сразу под кружком, она ляжет прямо на пунктир.
          const below = y > CENTER
          const labelY = below ? y + SATELLITE + 15 : y - SATELLITE - 11
          return (
            <g key={`label-${slot.id}`}>
              <text x={x} y={labelY} textAnchor="middle" className="seal-label" fill={slot.sealed ? '#6F6D7A' : '#A9A6AE'}>
                {slot.label}
              </text>
              {slot.note && (
                <text x={x} y={labelY + 12} textAnchor="middle" className="seal-note" fill="#55535f">
                  {slot.note}
                </text>
              )}
            </g>
          )
        })}
    </svg>
  )
}

/** Знаки систем внутри спутников. Штрихи не тоньше 1.5 — тоньше пропадает на телефоне. */
export const SEAL_GLYPHS: Record<SealSlotId, (color: string) => ReactNode> = {
  ziwei: () => <path d="M24 11.5l3.2 9.3 9.3 3.2-9.3 3.2-3.2 9.3-3.2-9.3-9.3-3.2 9.3-3.2z" fill="#D8B36A" />,
  kin: (color) => (
    <>
      <path d="M24 14.5l9 9.5-9 9.5-9-9.5z" fill="none" stroke={color} strokeWidth="1.8" />
      <circle cx="24" cy="24" r="2.7" fill={color} />
    </>
  ),
  tone: (color) => (
    <>
      <circle cx="17" cy="18.5" r="2.7" fill={color} />
      <circle cx="24" cy="18.5" r="2.7" fill={color} />
      <circle cx="31" cy="18.5" r="2.7" fill={color} />
      <rect x="14" y="26" width="20" height="3.8" rx="1.8" fill={color} />
    </>
  ),
  iching: (color) => (
    <g fill={color}>
      <rect x="15" y="15.6" width="18" height="2.3" rx="1.1" />
      <rect x="15" y="20.6" width="7.4" height="2.3" rx="1.1" />
      <rect x="25.6" y="20.6" width="7.4" height="2.3" rx="1.1" />
      <rect x="15" y="25.6" width="18" height="2.3" rx="1.1" />
      <rect x="15" y="30.6" width="7.4" height="2.3" rx="1.1" />
      <rect x="25.6" y="30.6" width="7.4" height="2.3" rx="1.1" />
    </g>
  ),
  mewa: (color) => (
    <g stroke={color} strokeWidth="1.4">
      <line x1="15" y1="18" x2="33" y2="18" />
      <line x1="15" y1="24" x2="33" y2="24" />
      <line x1="15" y1="30" x2="33" y2="30" />
      <line x1="18" y1="15" x2="18" y2="33" />
      <line x1="24" y1="15" x2="24" y2="33" />
      <line x1="30" y1="15" x2="30" y2="33" />
    </g>
  ),
}
